import { ArchivedCompanyCard } from '@/components/archived-company-card';
import { ArchivesBreadcrumb } from '@/components/breadcrumbs';
import { DeleteCompanyDialog } from '@/components/delete-company-dialog';
import { ApiError, companiesApi } from '@/lib/api';
import type { ArchivedCompany } from '@mss_creator/shared';
import { Icon } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/companies/archives')({
  component: ArchivesPage,
  staticData: { breadcrumb: ArchivesBreadcrumb },
});

export const ARCHIVES_FORBIDDEN_MESSAGE = "Vous n'avez pas les droits pour afficher cette page.";
export const ARCHIVES_LOAD_ERROR_MESSAGE =
  'Impossible de charger les entreprises archivées. Réessayez plus tard.';

/**
 * The archived companies (issue #20, planche 03): reached from the « Archives
 * (N) » link of the grid, reserved to pms_user and pms_admin. Rights are the
 * API's (a 403 turns into the refusal message below) plus the hidden link:
 * the repo's convention, no frontend route guard.
 *
 * Exported so the tests can render it under a QueryClientProvider without a
 * router tree.
 */
export function ArchivesPage() {
  const [deleteTarget, setDeleteTarget] = useState<ArchivedCompany | null>(null);
  const archivedQuery = useQuery({
    queryKey: ['companies', 'archived'],
    queryFn: () => companiesApi.listArchived(),
  });

  return (
    <section className="space-y-6">
      <div>
        <h1 className="font-bold text-bleu-nuit text-h2">Entreprises archivées</h1>
        <p className="text-gray-600 text-small">
          Elles n'apparaissent plus dans la grille et leurs matrices sont fermées. Restaurez-les ou
          supprimez-les définitivement.
        </p>
      </div>

      <ArchivesBody
        isLoading={archivedQuery.isLoading}
        error={archivedQuery.error}
        companies={archivedQuery.data}
        onDelete={setDeleteTarget}
      />

      <DeleteCompanyDialog company={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </section>
  );
}

interface ArchivesBodyProps {
  isLoading: boolean;
  error: Error | null;
  companies: ArchivedCompany[] | undefined;
  onDelete: (company: ArchivedCompany) => void;
}

function ArchivesBody({ isLoading, error, companies, onDelete }: ArchivesBodyProps) {
  if (isLoading) {
    return <p className="text-gray-500 text-small">Chargement…</p>;
  }
  if (error || !companies) {
    const forbidden = error instanceof ApiError && error.code === 'ROLE_FORBIDDEN';
    return (
      <p role="alert" className="text-danger text-small">
        {forbidden ? ARCHIVES_FORBIDDEN_MESSAGE : ARCHIVES_LOAD_ERROR_MESSAGE}
      </p>
    );
  }
  if (companies.length === 0) {
    return <EmptyArchives />;
  }
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
      {companies.map((company) => (
        <ArchivedCompanyCard key={company.id} company={company} onDelete={onDelete} />
      ))}
    </div>
  );
}

function EmptyArchives() {
  return (
    <div className="mx-auto flex max-w-[480px] flex-col items-center gap-2 py-12 text-center">
      <span className="mb-1 flex size-14 items-center justify-center rounded-full bg-gray-100 text-gray-600">
        <Icon name="archive" size={24} />
      </span>
      <h2 className="font-bold text-body-lg text-noir">Aucune entreprise archivée.</h2>
      <p className="text-gray-700 text-small">
        Les entreprises que vous archivez depuis la grille apparaissent ici.
      </p>
      <Link
        to="/"
        className="mt-2 inline-flex items-center gap-1 font-semibold text-bleu-nuit text-small no-underline"
      >
        <Icon name="arrow_back" size={16} />
        Retour aux entreprises
      </Link>
    </div>
  );
}
